// src/components/public/branding/DynamicStyles.tsx
'use client'
import React, { useEffect } from 'react'
import { useTheme } from './ThemeProvider'
import { getTemplateConfig, type TemplateConfig } from '@/lib/templates/template-config'
import { hexToRgbTriplet as convertHexToRgb } from '@/lib/branding/hex-to-rgb'
import { sanitizeBrandingHtml } from '@/lib/branding/sanitize'

// Safe wrapper that falls back to original color if conversion fails
function hexToRgbTriplet(color: string): string {
  if (!color) return color
  const rgb = convertHexToRgb(color)
  return rgb || color
}

// Sanitize tracking IDs to prevent injection (allow only alphanumeric, hyphens, underscores)
function sanitizeTrackingId(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, '')
}

// Imperatively inject an external script tag into the document head.
// Deduplicates by element ID so the same script is never added twice.
function injectExternalScript(id: string, src: string): HTMLScriptElement | null {
  try {
    const existing = document.getElementById(id)
    if (existing) return existing as HTMLScriptElement
    const el = document.createElement('script')
    el.id = id
    el.async = true
    el.src = src
    document.head.appendChild(el)
    if (process.env.NODE_ENV !== 'production') console.log(`[DynamicStyles] Injected external script "${id}": ${src}`)
    return el
  } catch (err) {
    console.error(`[DynamicStyles] Failed to inject script "${id}":`, err)
    return null
  }
}

// Execute inline JavaScript directly (more reliable than creating <script> elements
// with textContent, which can be blocked by CSP or fail in some rendering contexts).
// Deduplicates by tracking executed IDs on window.
function execOnce(id: string, code: string): void {
  try {
    const trackKey = '__ds_exec'
    const win = window as unknown as Record<string, Set<string>>
    if (!win[trackKey]) win[trackKey] = new Set()
    if (win[trackKey].has(id)) return
    win[trackKey].add(id)
    new Function(code)()
    if (process.env.NODE_ENV !== 'production') console.log(`[DynamicStyles] Executed inline code "${id}"`)
  } catch (err) {
    console.error(`[DynamicStyles] Failed to execute code "${id}":`, err)
  }
}

export function DynamicStyles() {
  const { branding } = useTheme()

  // Inject tracking scripts imperatively via useEffect.
  // Uses execOnce() for inline code (direct execution via new Function) and
  // injectExternalScript() for external script tags, both with deduplication.
  useEffect(() => {
    if (!branding) {
      if (process.env.NODE_ENV !== 'production') console.log('[DynamicStyles] No branding data yet, skipping tracking injection')
      return
    }
    const codes = branding.customizations?.trackingCodes
    if (process.env.NODE_ENV !== 'production') {
      console.log('[DynamicStyles] Tracking codes from branding:', JSON.stringify(codes))
    }
    if (!codes) return

    // Google Analytics 4
    if (codes.googleAnalytics) {
      const gaId = sanitizeTrackingId(codes.googleAnalytics)
      if (gaId) {
        injectExternalScript('ga4-loader', `https://www.googletagmanager.com/gtag/js?id=${gaId}`)
        execOnce('ga4-config', `window.dataLayer=window.dataLayer||[];window.gtag=function(){dataLayer.push(arguments);};window.gtag('js',new Date());window.gtag('config','${gaId}');`)
      }
    }

    // Google Tag Manager
    if (codes.googleTagManager) {
      const gtmId = sanitizeTrackingId(codes.googleTagManager)
      if (gtmId) {
        execOnce('gtm-loader', `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`)
      }
    }

    // Facebook Pixel
    if (codes.facebookPixel) {
      const fbId = sanitizeTrackingId(codes.facebookPixel)
      if (fbId) {
        execOnce('fb-pixel', `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${fbId}');fbq('track','PageView');`)
      }
    }

    // TikTok Pixel
    if (codes.tikTokPixel) {
      const ttId = sanitizeTrackingId(codes.tikTokPixel)
      if (ttId) {
        execOnce('tt-pixel', `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${ttId}');ttq.page();}(window,document,'ttq');`)
      }
    }

    // Custom scripts from admin analytics settings
    if (codes.customScripts && Array.isArray(codes.customScripts)) {
      codes.customScripts.forEach((script: string, index: number) => {
        if (script && script.trim()) {
          execOnce(`custom-script-${index}`, script.trim())
        }
      })
    }
  }, [branding])

  // Render nothing until branding loads. NOTE: do not output a different
  // shape (like a different style tag) — that causes a hydration mismatch
  // since server and client first render don't agree on what branding is.
  // The previous "body { opacity: 0 }" loading style also produced
  // blank-page reports when branding fetch failed or stalled.
  if (!branding) return null

  // Generate layout CSS variables and overrides from template config properties
  const generateTemplateCSS = (template: TemplateConfig) => {
    // Spacing multiplier based on card spacing
    const spacingMultiplier =
      template.homepage.cardSpacing === 'relaxed' ? '1.5' :
      template.homepage.cardSpacing === 'tight' ? '0.75' :
      '1'

    // Card gap from card spacing
    const cardGap =
      template.homepage.cardSpacing === 'tight' ? '12px' :
      template.homepage.cardSpacing === 'relaxed' ? '24px' :
      '16px'

    // Hero height
    const heroHeight =
      template.homepage.heroHeight === 'compact' ? '200px' :
      template.homepage.heroHeight === 'tall' ? '500px' :
      '350px'

    // Hover effect CSS based on eventCard.hoverEffect
    const hoverCSS =
      template.eventCard.hoverEffect === 'scale' ? `
        .event-card:hover {
          transform: scale(1.02) translateY(-4px) !important;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2) !important;
        }
      ` :
      template.eventCard.hoverEffect === 'lift' ? `
        .event-card:hover {
          transform: translateY(-2px) !important;
        }
      ` :
      // 'none' — subtle shadow only
      `
        .event-card:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
        }
      `

    // Narrow page width for 2-column + relaxed spacing configs
    const narrowPageCSS =
      template.homepage.gridColumns.xl <= 2 && template.homepage.cardSpacing === 'relaxed' ? `
        .max-w-page {
          max-width: 1040px !important;
        }
      ` : ''

    // List layout CSS for single-column grids
    const listLayoutCSS =
      template.homepage.gridColumns.xl === 1 ? `
        .event-card-container {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
      ` : ''

    return `
      :root {
        --template-spacing: ${spacingMultiplier};
        --template-card-gap: ${cardGap};
        --hero-height: ${heroHeight};
      }
      ${hoverCSS}
      ${narrowPageCSS}
      ${listLayoutCSS}
    `
  }

  // Generate CSS custom properties from branding data
  const generateCSS = () => {
    const { colors, typography, layout } = branding
    const template = getTemplateConfig(layout)
    // Dark mode: true by default, treat undefined as true for backwards compatibility
    const isDark = branding.customizations.enableDarkMode !== false

    return `
      :root {
        /* Colors (RGB triplets for Tailwind alpha support) */
        --color-primary: ${hexToRgbTriplet(colors.primary)};
        --color-primary-hover: ${hexToRgbTriplet(colors.primaryHover)};
        --color-primary-light: ${hexToRgbTriplet(colors.primaryLight)};
        --color-primary-dark: ${hexToRgbTriplet(colors.primaryDark)};
        --color-secondary: ${hexToRgbTriplet(colors.secondary)};
        --color-accent: ${hexToRgbTriplet(colors.accent)};
        --color-background: ${isDark ? '17 24 39' : hexToRgbTriplet(colors.background)};
        --color-foreground: ${isDark ? '249 250 251' : hexToRgbTriplet(colors.foreground)};
        --color-muted: ${isDark ? '31 41 55' : hexToRgbTriplet(colors.muted)};
        --color-card-background: ${isDark ? '31 41 55' : hexToRgbTriplet(colors.cardBackground)};
        --color-border: ${isDark ? '55 65 81' : hexToRgbTriplet(colors.borderColor)};
        --color-success: ${hexToRgbTriplet(colors.success)};
        --color-warning: ${hexToRgbTriplet(colors.warning)};
        --color-error: ${hexToRgbTriplet(colors.error)};

        /* Typography */
        --font-heading: ${typography.headingFont}, ${typography.fontFallbacks.heading};
        --font-body: ${typography.bodyFont}, ${typography.fontFallbacks.body};
        --font-weight-heading: ${typography.headingWeight};
        --font-weight-body: ${typography.bodyWeight};
        --letter-spacing: ${typography.letterSpacing};
        --line-height: ${typography.lineHeight};

        /* Layout */
        --container-width: ${layout.containerWidth};
        --border-radius: ${layout.borderRadius === 'sm' ? '0.125rem' : 
                          layout.borderRadius === 'md' ? '0.375rem' : 
                          layout.borderRadius === 'lg' ? '0.5rem' : 
                          layout.borderRadius === 'xl' ? '0.75rem' : '0.5rem'};
        --shadow-style: ${layout.shadowStyle === 'none' ? 'none' : 
                        layout.shadowStyle === 'sm' ? '0 1px 2px 0 rgb(0 0 0 / 0.05)' :
                        layout.shadowStyle === 'md' ? '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' :
                        layout.shadowStyle === 'lg' ? '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' :
                        '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'};
      }

      /* Apply fonts to elements */
      body {
        font-family: var(--font-body);
        font-weight: var(--font-weight-body);
        color: rgb(var(--color-foreground));
        background-color: rgb(var(--color-surface-secondary));
        opacity: 1;
        transition: opacity 0.15s ease;
      }

      h1, h2, h3, h4, h5, h6 {
        font-family: var(--font-heading);
        font-weight: var(--font-weight-heading);
        letter-spacing: var(--letter-spacing);
        line-height: var(--line-height);
      }

      /* Override default button styles with branding */
      button[style*="background-color"] {
        border-radius: var(--border-radius);
      }

      /* Card styles */
      .card {
        background-color: rgb(var(--color-card-background));
        border: 1px solid rgb(var(--color-border));
        border-radius: var(--border-radius);
        box-shadow: var(--shadow-style);
      }

      /* Container width override */
      .container {
        max-width: var(--container-width);
      }

      /* Custom animations based on branding settings */
      ${branding.customizations.enableAnimations ? `
        * {
          transition-property: color, background-color, border-color, box-shadow;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }

        /* Apply transform transitions only to interactive elements (NOT *)
           to prevent creating containing blocks that break position:fixed on iOS Safari */
        a, button, .btn-primary, .event-card {
          transition-property: color, background-color, border-color, box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }

        button:hover, .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        button:active, .btn-primary:active {
          transform: translateY(0);
        }
      ` : ''}

      /* Custom CSS from branding settings (sanitized) */
      ${(branding.customizations.customCSS || '').replace(/<[^>]*>/g, '').replace(/expression\s*\(/gi, '').replace(/javascript\s*:/gi, '').replace(/url\s*\(\s*['"]?\s*javascript/gi, '')}

      /* Header style adjustments - higher specificity */
      header.public-header,
      .public-header {
        ${layout.headerStyle === 'transparent' ? `
          background-color: ${isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)'} !important;
          backdrop-filter: blur(10px) !important;
          color: rgb(var(--color-foreground)) !important;
          border-bottom: 1px solid rgb(var(--color-border)) !important;
        ` : layout.headerStyle === 'surface' ? `
          background-color: rgb(var(--color-header-bg, var(--color-surface))) !important;
          background-image: none !important;
          color: rgb(var(--color-foreground)) !important;
          border-bottom: 1px solid rgb(var(--color-border)) !important;
        ` : layout.headerStyle === 'solid' ? `
          background-color: rgb(var(--color-header-bg, var(--color-primary))) !important;
          background-image: none !important;
          color: rgb(var(--color-header-text, 255 255 255)) !important;
        ` : layout.headerStyle === 'gradient' ? `
          background: linear-gradient(135deg, rgb(var(--color-header-bg, var(--color-primary))), rgb(var(--color-accent))) !important;
          background-image: linear-gradient(135deg, rgb(var(--color-header-bg, var(--color-primary))), rgb(var(--color-accent))) !important;
          color: rgb(var(--color-header-text, 255 255 255)) !important;
        ` : `
          background-color: rgb(var(--color-header-bg, var(--color-primary))) !important;
          color: rgb(var(--color-header-text, 255 255 255)) !important;
        `}
        /* Let the navbar's own classes govern stacking + positioning
           (sticky top-0 z-40). Forcing z-index:1000/position:relative here
           broke sticky and pushed the header above the cart drawer (z-50). */
        transition: all 0.3s ease !important;
      }

      /* Enhanced header text contrast for light colors */
      .public-header a,
      .public-header button,
      .public-header span,
      .public-header div {
        ${(layout.headerStyle === 'transparent' || layout.headerStyle === 'surface') ? `
          color: rgb(var(--color-foreground)) !important;
        ` : `
          color: white !important;
        `}
      }

      /* Force all header content to have proper contrast */
      .public-header * {
        ${(layout.headerStyle === 'transparent' || layout.headerStyle === 'surface') ? `
          color: rgb(var(--color-foreground)) !important;
        ` : `
          color: white !important;
        `}
      }

      /* Override any conflicting Tailwind classes */
      .public-header .text-slate-600,
      .public-header .text-gray-600,
      .public-header .text-slate-900 {
        ${(layout.headerStyle === 'transparent' || layout.headerStyle === 'surface') ? `
          color: rgb(var(--color-foreground)) !important;
        ` : `
          color: white !important;
        `}
      }

      /* Header hover states */
      .public-header a:hover,
      .public-header button:hover {
        ${layout.headerStyle === 'transparent' ? `
          background-color: rgb(var(--color-primary)) !important;
          color: white !important;
        ` : `
          background-color: rgba(255, 255, 255, 0.1) !important;
        `}
        border-radius: var(--border-radius);
        transition: all 0.2s ease;
      }

      /* Button style adjustments */
      .btn-primary {
        background-color: rgb(var(--color-primary));
        border-color: rgb(var(--color-primary));
        color: rgb(var(--color-text-inverse));
        ${layout.buttonStyle === 'rounded' ? 'border-radius: 9999px;' :
          layout.buttonStyle === 'square' ? 'border-radius: 0;' :
          'border-radius: var(--border-radius);'}
      }

      .btn-primary:hover {
        background-color: rgb(var(--color-primary-hover));
        border-color: rgb(var(--color-primary-hover));
      }

      /* Template-specific styles */
      ${generateTemplateCSS(template)}

      /* Card style from Layout tab (flat/bordered/elevated) */
      ${layout.cardStyle === 'flat' ? `
        .card, .event-card {
          box-shadow: none !important;
          border: none !important;
        }
      ` : ''}
      ${layout.cardStyle === 'bordered' ? `
        .card, .event-card {
          box-shadow: none !important;
          border: 1px solid rgb(var(--color-surface-border)) !important;
        }
      ` : ''}
      ${layout.cardStyle === 'elevated' ? `
        .card, .event-card {
          box-shadow: var(--shadow-style) !important;
          border: none !important;
        }
      ` : ''}

      /* Spacing from Layout tab (compact/normal/spacious) */
      ${layout.spacing === 'compact' ? `
        .p-4 { padding: 0.75rem !important; }
        .p-6 { padding: 1rem !important; }
        .gap-5 { gap: 0.75rem !important; }
        .gap-8 { gap: 1rem !important; }
        .space-y-8 > * + * { margin-top: 1rem !important; }
      ` : ''}
      ${layout.spacing === 'spacious' ? `
        .p-4 { padding: 1.5rem !important; }
        .p-6 { padding: 2rem !important; }
        .gap-5 { gap: 2rem !important; }
        .gap-8 { gap: 3rem !important; }
        .space-y-8 > * + * { margin-top: 3rem !important; }
      ` : ''}

      /* RTL support */
      ${branding.customizations.enableRTL ? `
        html[dir="rtl"] {
          direction: rtl;
        }
        
        html[dir="rtl"] .text-left {
          text-align: right;
        }
        
        html[dir="rtl"] .text-right {
          text-align: left;
        }
      ` : ''}

      /* Theme mode — override ALL design system variables */
      ${isDark ? `
      :root {
        /* Branding overrides */
        --color-background: 17 24 39 !important;
        --color-foreground: 249 250 251 !important;
        --color-card-background: 31 41 55 !important;
        --color-border: 55 65 81 !important;
        --color-muted: 31 41 55 !important;

        /* Surface overrides */
        --color-surface: 15 23 42 !important;
        --color-surface-secondary: 2 6 23 !important;
        --color-surface-tertiary: 30 41 59 !important;
        --color-surface-border: 51 65 85 !important;
        --color-surface-border-light: 30 41 59 !important;

        /* Text overrides */
        --color-text-primary: 241 245 249 !important;
        --color-text-secondary: 203 213 225 !important;
        --color-text-tertiary: 100 116 139 !important;
        --color-text-inverse: 15 23 42 !important;

        /* Primary accent for dark */
        --color-primary: 226 232 240 !important;
        --color-primary-hover: 241 245 249 !important;
        --color-primary-light: 30 41 59 !important;
        --color-primary-50: 30 41 59 !important;
        --color-primary-100: 51 65 85 !important;
        --color-primary-500: 226 232 240 !important;
        --color-primary-600: 203 213 225 !important;
        --color-primary-700: 241 245 249 !important;

        /* Semantic colors for dark */
        --color-success: 34 197 94 !important;
        --color-success-light: 5 46 22 !important;
        --color-warning: 249 115 22 !important;
        --color-warning-light: 67 20 7 !important;
        --color-error: 239 68 68 !important;
        --color-error-light: 69 10 10 !important;
        --color-info: 59 130 246 !important;
        --color-info-light: 23 37 84 !important;

        /* Extra tokens */
        --color-hero-bg: 2 6 23 !important;
        --color-muted-bg: 30 41 59 !important;
        --color-muted-text: 203 213 225 !important;

        /* Shadows for dark */
        --shadow-card: 0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2) !important;
        --shadow-card-hover: 0 4px 12px rgba(0,0,0,0.4) !important;
        --shadow-panel: 0 4px 20px rgba(0,0,0,0.4) !important;
        --shadow-navbar: 0 1px 3px rgba(0,0,0,0.3) !important;
        --shadow-modal: 0 16px 48px rgba(0,0,0,0.5) !important;
        --shadow-sticky: 0 -2px 10px rgba(0,0,0,0.3) !important;
      }
      ` : `
      :root {
        /* Light mode overrides */
        --color-background: 255 255 255 !important;
        --color-foreground: 17 24 39 !important;
        --color-card-background: 255 255 255 !important;
        --color-border: 229 231 235 !important;
        --color-muted: 243 244 246 !important;

        /* Surface overrides */
        --color-surface: 255 255 255 !important;
        --color-surface-secondary: 247 248 250 !important;
        --color-surface-tertiary: 240 241 244 !important;
        --color-surface-border: 229 231 235 !important;
        --color-surface-border-light: 240 241 244 !important;

        /* Text overrides */
        --color-text-primary: 17 24 39 !important;
        --color-text-secondary: 75 85 99 !important;
        --color-text-tertiary: 156 163 175 !important;
        --color-text-inverse: 255 255 255 !important;

        /* Primary accent for light */
        --color-primary: 17 24 39 !important;
        --color-primary-hover: 55 65 81 !important;
        --color-primary-light: 249 250 251 !important;
        --color-primary-50: 249 250 251 !important;
        --color-primary-100: 243 244 246 !important;
        --color-primary-500: 17 24 39 !important;
        --color-primary-600: 31 41 55 !important;
        --color-primary-700: 55 65 81 !important;

        /* Semantic colors for light */
        --color-success: 22 163 74 !important;
        --color-success-light: 240 253 244 !important;
        --color-warning: 234 88 12 !important;
        --color-warning-light: 255 247 237 !important;
        --color-error: 220 38 38 !important;
        --color-error-light: 254 242 242 !important;
        --color-info: 37 99 235 !important;
        --color-info-light: 239 246 255 !important;

        /* Extra tokens */
        --color-hero-bg: 243 244 246 !important;
        --color-muted-bg: 243 244 246 !important;
        --color-muted-text: 55 65 81 !important;
        --skeleton-shimmer: rgba(0, 0, 0, 0.06) !important;

        /* Shadows for light */
        --shadow-card: 0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06) !important;
        --shadow-card-hover: 0 4px 12px rgba(0,0,0,0.12) !important;
        --shadow-panel: 0 4px 20px rgba(0,0,0,0.08) !important;
        --shadow-navbar: 0 1px 3px rgba(0,0,0,0.05) !important;
        --shadow-modal: 0 16px 48px rgba(0,0,0,0.12) !important;
        --shadow-sticky: 0 -2px 10px rgba(0,0,0,0.06) !important;
      }
      `}

      /* Layer 3: Admin color overrides — only for colors that differ from defaults */
      ${(() => {
        const darkDefaults: Record<string, string> = {
          primary: '#e2e8f0', primaryHover: '#f1f5f9', accent: '#e2e8f0',
          background: '#111827', foreground: '#f9fafb',
          success: '#22c55e', warning: '#f97316', error: '#ef4444',
          muted: '#1f2937', cardBackground: '#1f2937', borderColor: '#374151',
          secondary: '#cbd5e1', primaryLight: '#1e293b', primaryDark: '#f1f5f9',
        }
        const lightDefaults: Record<string, string> = {
          primary: '#111827', primaryHover: '#374151', accent: '#111827',
          background: '#ffffff', foreground: '#111827',
          success: '#16a34a', warning: '#ea580c', error: '#dc2626',
          muted: '#f3f4f6', cardBackground: '#ffffff', borderColor: '#e5e7eb',
          secondary: '#4b5563', primaryLight: '#f9fafb', primaryDark: '#030712',
        }
        const defs = isDark ? darkDefaults : lightDefaults
        const map: Record<string, string> = {
          primary: '--color-primary', primaryHover: '--color-primary-hover', accent: '--color-accent',
          background: '--color-background', foreground: '--color-foreground',
          success: '--color-success', warning: '--color-warning', error: '--color-error',
          muted: '--color-muted', cardBackground: '--color-card-background', borderColor: '--color-border',
        }
        const ov: string[] = []
        for (const [key, cssVar] of Object.entries(map)) {
          const val = (colors as Record<string, string>)[key]
          if (val && val.toLowerCase() !== defs[key]?.toLowerCase()) {
            const rgb = hexToRgbTriplet(val)
            if (rgb) ov.push(`${cssVar}: ${rgb} !important`)
          }
        }

        // Design-system tokens. The theme-mode block above pins the variables
        // the storefront actually consumes (--color-surface*, --color-text-*,
        // --color-primary-500/600/700...) with !important, so customized
        // colors must be re-pinned here or they never render. "Customized"
        // means differs from BOTH mode palettes — untouched clients keep
        // byte-identical CSS.
        const triplet = (hex: string): string | null => {
          const t = hexToRgbTriplet(hex)
          return /^\d{1,3} \d{1,3} \d{1,3}$/.test(t) ? t : null
        }
        const custom = (key: string): string | null => {
          const val = (colors as Record<string, string>)[key]
          if (!val || typeof val !== 'string') return null
          const lc = val.toLowerCase()
          if (lc === lightDefaults[key]?.toLowerCase() || lc === darkDefaults[key]?.toLowerCase()) return null
          return triplet(val) ? val : null
        }
        // Auto-contrast: determine if a hex color is dark or light
        const isLightColor = (hex: string): boolean => {
          const t = triplet(hex)
          if (!t) return false
          const [r, g, b] = t.split(' ').map(Number)
          return (r * 299 + g * 587 + b * 114) / 1000 > 128
        }
        const mixToward = (hex: string, target: [number, number, number], ratio: number): string | null => {
          const t = triplet(hex)
          if (!t) return null
          return t
            .split(' ')
            .map((v, i) => Math.round(Number(v) + (target[i] - Number(v)) * ratio))
            .join(' ')
        }
        const WHITE: [number, number, number] = [255, 255, 255]
        const BLACK: [number, number, number] = [0, 0, 0]

        // Brand primary scale: -500 mirrors the brand color; -600/-700 use the
        // stored dark/hover colors when a palette preset set them, else shades
        // derived from the brand color (lighter in dark mode, darker in light
        // mode) so a single Brand Color pick restyles buttons, links and
        // badges coherently.
        const brandPrimary = custom('primary')
        const primary500 = brandPrimary ? triplet(brandPrimary) : null
        if (brandPrimary && primary500) {
          // Hover/darker shades: normally lighten in dark mode — but a LIGHT
          // primary lightened trends to white and hover states vanish, so
          // light primaries always darken instead. An explicitly customized
          // primaryHover (admin "Button Hover Color") bypasses all of this.
          const shadeTarget = isDark && !isLightColor(brandPrimary) ? WHITE : BLACK
          const tintTarget = isDark ? BLACK : WHITE
          const shade600 =
            (custom('primaryDark') && triplet(custom('primaryDark') as string)) ||
            mixToward(brandPrimary, shadeTarget, 0.12)
          const shade700 =
            (custom('primaryHover') && triplet(custom('primaryHover') as string)) ||
            mixToward(brandPrimary, shadeTarget, 0.24)
          // Stored primaryLight values come from light-tuned palettes; in dark
          // mode a light tint would make -50 badge backgrounds illegible, so
          // only honor it when it is itself dark — else derive.
          const storedLight = custom('primaryLight')
          const tint50 =
            (storedLight && (!isDark || !isLightColor(storedLight)) && triplet(storedLight)) ||
            mixToward(brandPrimary, tintTarget, isDark ? 0.75 : 0.92)
          const tint100 = mixToward(brandPrimary, tintTarget, isDark ? 0.6 : 0.85)
          ov.push(`--color-primary-500: ${primary500} !important`)
          if (shade600) ov.push(`--color-primary-600: ${shade600} !important`)
          if (shade700) ov.push(`--color-primary-700: ${shade700} !important`)
          if (tint50) ov.push(`--color-primary-50: ${tint50} !important`, `--color-primary-light: ${tint50} !important`)
          if (tint100) ov.push(`--color-primary-100: ${tint100} !important`)
          // The legacy loop above only writes --color-primary-hover when
          // primaryHover itself was customized; keep hover on-brand otherwise.
          if (!custom('primaryHover') && shade600) ov.push(`--color-primary-hover: ${shade600} !important`)
        }

        // Surface + text tokens. Dark mode only accepts values that keep the
        // theme legible (dark backgrounds, light text); light mode applies
        // customized values directly.
        const surfaceTokens: Array<[string, string[], 'bg' | 'text' | 'border']> = [
          ['background', ['--color-surface'], 'bg'],
          ['muted', ['--color-surface-secondary', '--color-muted-bg', '--color-hero-bg'], 'bg'],
          ['cardBackground', ['--color-surface-tertiary'], 'bg'],
          ['borderColor', ['--color-surface-border', '--color-surface-border-light'], 'border'],
          ['foreground', ['--color-text-primary'], 'text'],
          ['secondary', ['--color-text-secondary', '--color-muted-text'], 'text'],
        ]
        for (const [key, cssVars, kind] of surfaceTokens) {
          const val = custom(key)
          if (!val) continue
          if (isDark && kind === 'bg' && isLightColor(val)) continue
          if (isDark && kind === 'text' && !isLightColor(val)) continue
          const rgb = triplet(val)
          if (rgb) for (const cssVar of cssVars) ov.push(`${cssVar}: ${rgb} !important`)
        }

        let extraCSS = ''

        // Header color override
        const headerBg = (colors as any).headerBackground
        const headerText = (colors as any).headerText || (headerBg ? (isLightColor(headerBg) ? '#111827' : '#ffffff') : null)
        if (headerBg) {
          const rgb = hexToRgbTriplet(headerBg)
          if (rgb) extraCSS += `nav.sticky { background-color: rgb(${rgb}) !important; border-bottom-color: rgb(${rgb}) !important; }`
        }
        if (headerText) {
          const rgb = hexToRgbTriplet(headerText)
          if (rgb) {
            extraCSS += `nav.sticky.sticky.sticky, nav.sticky.sticky.sticky a, nav.sticky.sticky.sticky button, nav.sticky.sticky.sticky span, nav.sticky.sticky.sticky p, nav.sticky.sticky.sticky div { color: rgb(${rgb}) !important; }`
            extraCSS += `nav.sticky.sticky.sticky svg { color: rgb(${rgb}) !important; stroke: rgb(${rgb}) !important; }`
            extraCSS += `nav.sticky.sticky.sticky [class*="text-text-"] { color: rgb(${rgb}) !important; }`
          }
        }
        // Search bar overrides. The mobile search box renders below the hero
        // (outside nav.sticky) with the .event-search wrapper — without that
        // selector it kept default colors while the desktop navbar search
        // followed the branding overrides.
        if ((colors as any).searchBackground) {
          const rgb = hexToRgbTriplet((colors as any).searchBackground)
          if (rgb) extraCSS += `nav.sticky input, nav.sticky select, nav.sticky [class*="search"], nav.sticky [class*="filter"], .event-search input, .event-search select { background-color: rgb(${rgb}) !important; }`
        }
        if ((colors as any).searchText) {
          const rgb = hexToRgbTriplet((colors as any).searchText)
          if (rgb) {
            // Everything INSIDE a search bar follows this one colour: the typed
            // text, the placeholder, the dropdown values and their options, and
            // the icons (magnifier, location pin, chevron). Previously only the
            // input and select text moved, so a tenant who set a light search
            // colour got readable text next to icons still sitting at their
            // default grey.
            //
            // Icons are strokes, not fills, so `color` alone does nothing for
            // them — currentColor only helps where the SVG asks for it. Set
            // stroke explicitly, and leave fill alone so the icons stay
            // outlines rather than turning into solid blocks.
            extraCSS += `[data-search-bar] input, [data-search-bar] select, [data-search-bar] option, [data-search-bar] textarea, nav.sticky input, nav.sticky select, .event-search input, .event-search select { color: rgb(${rgb}) !important; }`
            extraCSS += `[data-search-bar] input::placeholder, nav.sticky input::placeholder, .event-search input::placeholder { color: rgb(${rgb}) !important; opacity: 0.65 !important; }`
            extraCSS += `[data-search-bar] svg { color: rgb(${rgb}) !important; stroke: rgb(${rgb}) !important; }`
          }
        }
        // Customer-accounts area color overrides — scoped CSS variables on
        // .accounts-scope (the accounts layout wrapper), so the storefront
        // theme is untouched. Empty value = inherit.
        {
          const acctBg = hexToRgbTriplet((colors as any).accountsBackground)
          const acctSurface = hexToRgbTriplet((colors as any).accountsSurface)
          const acctText = hexToRgbTriplet((colors as any).accountsText)
          const acctAccent = hexToRgbTriplet((colors as any).accountsAccent)
          let acctVars = ''
          if (acctBg) acctVars += `--color-background: ${acctBg};`
          if (acctSurface) acctVars += `--color-surface: ${acctSurface}; --color-surface-secondary: ${acctSurface};`
          if (acctText) acctVars += `--color-text-primary: ${acctText};`
          if (acctAccent) acctVars += `--color-primary: ${acctAccent};`
          if (acctVars) extraCSS += `.accounts-scope { ${acctVars} }`
          if (acctBg) extraCSS += `.accounts-scope { background-color: rgb(${acctBg}); }`
        }
        // Cart button color override
        if ((colors as any).cartButtonColor) {
          const rgb = hexToRgbTriplet((colors as any).cartButtonColor)
          if (rgb) extraCSS += `nav.sticky.sticky.sticky [aria-label*="cart" i], nav.sticky.sticky.sticky [aria-label*="Cart" i], nav.sticky.sticky.sticky button:last-child { color: rgb(${rgb}) !important; }`
          if (rgb) extraCSS += `nav.sticky.sticky.sticky [aria-label*="cart" i] svg, nav.sticky.sticky.sticky button:last-child svg { color: rgb(${rgb}) !important; stroke: rgb(${rgb}) !important; }`
        }
        // Account button color override
        if ((colors as any).accountButtonColor) {
          const rgb = hexToRgbTriplet((colors as any).accountButtonColor)
          if (rgb) extraCSS += `nav.sticky.sticky.sticky a[href*="login"], nav.sticky.sticky.sticky a[href*="account"] { color: rgb(${rgb}) !important; }`
          if (rgb) extraCSS += `nav.sticky.sticky.sticky a[href*="login"] svg, nav.sticky.sticky.sticky a[href*="account"] svg { color: rgb(${rgb}) !important; stroke: rgb(${rgb}) !important; }`
        }
        // Footer color overrides (mirrors the header mechanism). The footer
        // carries a `.public-footer` class. Force the background, and cascade
        // the text color to every child so links/headings/copyright follow it.
        {
          const footerBg = (colors as any).footerBackground
          const footerText = (colors as any).footerText || (footerBg ? (isLightColor(footerBg) ? '#111827' : '#ffffff') : null)
          if (footerBg) {
            const rgb = hexToRgbTriplet(footerBg)
            if (rgb) extraCSS += `.public-footer { background-color: rgb(${rgb}) !important; }`
          }
          if (footerText) {
            const rgb = hexToRgbTriplet(footerText)
            if (rgb) extraCSS += `.public-footer, .public-footer * { color: rgb(${rgb}) !important; }`
          }
        }

        const rootCSS = ov.length > 0 ? `:root { ${ov.join('; ')}; }` : ''
        return rootCSS + extraCSS
      })()}

      /* Main content width — respect the admin-configured containerWidth
         (branding.layout.containerWidth) while never exceeding the viewport.
         Placed last so an explicit container width wins over template presets
         (e.g. the 2-column "narrowPageCSS" override above). */
      .max-w-page {
        max-width: min(100%, var(--container-width, 1280px)) !important;
      }
    `
  }

  return (
    <>
      <style 
        key={`branding-${branding.colors.primary}-${branding.layout.headerStyle}`}
        dangerouslySetInnerHTML={{ __html: generateCSS() }} 
      />
      
      {/* Load Google Fonts — always include the selected heading + body fonts
          (not just the googleFonts list), otherwise picking e.g. "Playfair
          Display" sets --font-heading but the font file never loads and it
          silently falls back to a system font. Weights 400–800 so the Font
          Weight settings have faces to use. System/generic stacks are skipped. */}
      {(() => {
        const families = Array.from(
          new Set(
            [
              branding.typography.headingFont,
              branding.typography.bodyFont,
              ...branding.typography.googleFonts,
            ]
              .filter((f): f is string => !!f && f.trim().length > 0)
              .map((f) => f.trim())
              .filter((f) => !/^(system-ui|sans-serif|serif|monospace|-apple-system|inherit)/i.test(f)),
          ),
        )
        if (families.length === 0) return null
        const query = families
          .map((f) => `family=${f.replace(/ /g, '+')}:wght@400;500;600;700;800`)
          .join('&')
        return (
          <link
            href={`https://fonts.googleapis.com/css2?${query}&display=${branding.typography.fontDisplay || 'swap'}`}
            rel="stylesheet"
          />
        )
      })()}

      {/* Custom HTML head content - DISABLED to prevent SSR issues
          If you need custom HTML injection, implement it server-side or use a different approach
      {branding.customizations.headerHTML && (
        <div dangerouslySetInnerHTML={{ __html: sanitizeBrandingHtml(branding.customizations.headerHTML) }} />
      )}
      */}

      {/* Tracking scripts are injected imperatively via useEffect above */}
    </>
  )
}

export default DynamicStyles